import type { Unit } from '../types.js';

/**
 * The platform's one unit-of-measure list. Every API schema, frontend picker
 * and data-point validation reads it; there is no units table.
 *
 * Edit it by hand. A new unit is accepted everywhere at once — the legacy
 * public API (`LEGACY_PUBLIC_API_UNIT_VALUES`) excepted. Leave `system` unset
 * unless the unit belongs in the metric/imperial pickers.
 */
export const UNITS = Object.freeze([
  // Volume
  {
    value: 'LITRE',
    label: 'l',
    search: 'Litre',
    quantity: 'volume',
    system: 'metric',
  },
  {
    value: 'MILLILITRE',
    label: 'ml',
    search: 'Millilitre',
    quantity: 'volume',
    system: 'metric',
  },
  {
    value: 'CENTILITRE',
    label: 'cl',
    search: 'Centilitre',
    quantity: 'volume',
    system: 'metric',
  },
  {
    value: 'CUBIC_DECIMETRE',
    label: 'dm³',
    search: 'Cubic decimetre',
    quantity: 'volume',
    system: 'metric',
  },
  {
    value: 'CUBIC_METRE',
    label: 'm³',
    search: 'Cubic metre',
    quantity: 'volume',
    system: 'metric',
  },
  { value: 'GALLON', label: 'Gallon', search: 'Gallon', quantity: 'volume' },
  { value: 'QUART', label: 'Quart', search: 'Quart', quantity: 'volume' },
  { value: 'PINT', label: 'Pint', search: 'Pint', quantity: 'volume' },
  { value: 'GILL', label: 'Gill', search: 'Gill', quantity: 'volume' },
  {
    value: 'FLUID_OUNCE',
    label: 'Fluid ounce',
    search: 'Fluid ounce',
    quantity: 'volume',
  },
  {
    value: 'FLUID_DRAM',
    label: 'Fluid dram',
    search: 'Fluid dram',
    quantity: 'volume',
  },
  { value: 'MINIM', label: 'Minim', search: 'Minim', quantity: 'volume' },
  { value: 'BUSHEL', label: 'Bushel', search: 'Bushel', quantity: 'volume' },
  {
    value: 'ACRE_FOOT',
    label: 'Acre foot',
    search: 'Acre foot',
    quantity: 'volume',
  },
  { value: 'CORD', label: 'Cord', search: 'Cord', quantity: 'volume' },
  {
    value: 'CUBIC_YARD',
    label: 'yd³',
    search: 'Cubic yard',
    quantity: 'volume',
  },
  {
    value: 'CUBIC_FOOT',
    label: 'ft³',
    search: 'Cubic feet',
    quantity: 'volume',
  },
  {
    value: 'CUBIC_INCH',
    label: 'in³',
    search: 'Cubic inch',
    quantity: 'volume',
  },
  { value: 'PECK', label: 'Peck', search: 'Peck', quantity: 'volume' },
  // Length
  {
    value: 'METRE',
    label: 'm',
    search: 'Metre',
    quantity: 'length',
    system: 'metric',
  },
  {
    value: 'MILLIMETRE',
    label: 'mm',
    search: 'Millimetre',
    quantity: 'length',
    system: 'metric',
  },
  {
    value: 'DECIMETRE',
    label: 'dm',
    search: 'Decimetre',
    quantity: 'length',
    system: 'metric',
  },
  {
    value: 'CENTIMETRE',
    label: 'cm',
    search: 'Centimetre',
    quantity: 'length',
    system: 'metric',
  },
  { value: 'INCH', label: 'in', search: 'Inch', quantity: 'length' },
  {
    value: 'NAUTICAL_MILE',
    label: 'Nautical mile',
    search: 'Nautical mile',
    quantity: 'length',
  },
  { value: 'MILE', label: 'Mile', search: 'Mile', quantity: 'length' },
  { value: 'FURLONG', label: 'Furlong', search: 'Furlong', quantity: 'length' },
  { value: 'ROD', label: 'Rod', search: 'Rod', quantity: 'length' },
  { value: 'FATHOM', label: 'Fathom', search: 'Fathom', quantity: 'length' },
  { value: 'YARD', label: 'yd', search: 'Yard', quantity: 'length' },
  { value: 'FOOT', label: 'ft', search: 'Foot', quantity: 'length' },
  // Weight or mass
  {
    value: 'GRAM',
    label: 'g',
    search: 'Gram',
    quantity: 'weight',
    system: 'metric',
  },
  {
    value: 'GRAM_PER_SQUARE_METER',
    label: 'g/m²',
    search: 'Grams per square meter',
    quantity: 'weight',
    system: 'metric',
  },
  {
    value: 'GRAM_PER_SQUARE_FOOT',
    label: 'g/ft²',
    search: 'Grams per square foot',
    quantity: 'weight',
    system: 'metric',
  },
  {
    value: 'GRAM_PER_SQUARE_YARD',
    label: 'g/yd²',
    search: 'Grams per square yard',
    quantity: 'weight',
    system: 'metric',
  },
  {
    value: 'OUNCE_PER_SQUARE_YARD',
    label: 'oz/yd²',
    search: 'Ounce per square yard',
    quantity: 'weight',
    system: 'imperial',
  },
  {
    value: 'MILLIGRAM',
    label: 'mg',
    search: 'Milligram',
    quantity: 'weight',
    system: 'metric',
  },
  {
    value: 'KILOGRAM',
    label: 'kg',
    search: 'Kilogram',
    quantity: 'weight',
    system: 'metric',
  },
  {
    value: 'METRIC_TON',
    label: 't',
    search: 'Tonne',
    quantity: 'weight',
    system: 'metric',
  },
  {
    value: 'POUNDS',
    label: 'lb',
    search: 'Pounds',
    quantity: 'weight',
    system: 'imperial',
  },
  {
    value: 'SHORT_TON',
    label: 'Short ton',
    search: 'Short ton',
    quantity: 'weight',
  },
  {
    value: 'LONG_TON',
    label: 'Long ton',
    search: 'Long ton',
    quantity: 'weight',
  },
  {
    value: 'HUNDREDWEIGHT',
    label: 'Hundredweight',
    search: 'Hundredweight',
    quantity: 'weight',
  },
  {
    value: 'SHORT_HUNDREDWEIGHT',
    label: 'Short hundredweight',
    search: 'Short hundredweight',
    quantity: 'weight',
  },
  {
    value: 'LONG_HUNDREDWEIGHT',
    label: 'Long hundredweight',
    search: 'Long hundredweight',
    quantity: 'weight',
  },
  {
    value: 'OUNCE',
    label: 'oz',
    search: 'Ounce',
    quantity: 'weight',
    system: 'imperial',
  },
  { value: 'DRAM', label: 'Dram', search: 'Dram', quantity: 'weight' },
  { value: 'GRAIN', label: 'Grain', search: 'Grain', quantity: 'weight' },
  {
    value: 'PENNYWEIGHT',
    label: 'Pennyweight',
    search: 'Pennyweight',
    quantity: 'weight',
  },
  { value: 'SCRUPLE', label: 'Scruple', search: 'Scruple', quantity: 'weight' },
  { value: 'STONE', label: 'Stone', search: 'Stone', quantity: 'weight' },
  // Area
  {
    value: 'SQUARE_METRE',
    label: 'm²',
    search: 'Square metre',
    quantity: 'area',
    system: 'metric',
  },
  {
    value: 'SQUARE_DECIMETRE',
    label: 'dm²',
    search: 'Square decimetre',
    quantity: 'area',
    system: 'metric',
  },
  {
    value: 'SQUARE_CENTIMETRE',
    label: 'cm²',
    search: 'Square centimetre',
    quantity: 'area',
    system: 'metric',
  },
  {
    value: 'SQUARE_INCH',
    label: 'in²',
    search: 'Square inch',
    quantity: 'area',
  },
  {
    value: 'SQUARE_MILE',
    label: 'ml²',
    search: 'Square mile',
    quantity: 'area',
  },
  {
    value: 'SQUARE_FEET',
    label: 'ft²',
    search: 'Square feet',
    quantity: 'area',
  },
  {
    value: 'SQUARE_ROD',
    label: 'rod²',
    search: 'Square rod',
    quantity: 'area',
  },
  {
    value: 'SQUARE_YARD',
    label: 'yd²',
    search: 'Square yard',
    quantity: 'area',
  },
  { value: 'ACRE', label: 'ac', search: 'Acre', quantity: 'area' },
  // Count
  { value: 'PIECES', label: 'pcs', search: 'Pieces', quantity: 'count' },
  { value: 'PAIR', label: 'pr', search: 'Pair', quantity: 'count' },
  {
    value: 'PERCENTAGE',
    label: 'Percentage',
    search: 'Percentage',
    quantity: 'count',
  },
  { value: 'ROLL', label: 'Rolls', search: 'Rolls', quantity: 'count' },
  // Textile (yarn count)
  { value: 'DENIER', label: 'Denier', search: 'Denier', quantity: 'textile' },
  {
    value: 'JUTE',
    label: 'Jute (lbs/spyndle)',
    search: 'Jute',
    quantity: 'textile',
  },
  {
    value: 'ENGLISH_COUNT',
    label: 'English Count',
    search: 'English count',
    quantity: 'textile',
  },
  {
    value: 'METRIC_COUNT',
    label: 'Metric Count',
    search: 'Metric count',
    quantity: 'textile',
  },
  {
    value: 'WORSTED_COUNT',
    label: 'Worsted Count',
    search: 'Worsted count',
    quantity: 'textile',
  },
  { value: 'TEX', label: 'Tex', search: 'Tex', quantity: 'textile' },
  // Energy
  {
    value: 'KILOWATT_HOUR',
    label: 'kWh',
    search: 'Kilowatt hour',
    quantity: 'energy',
  },
  // Other
  { value: 'MOUND', label: 'Mound', search: 'Mound', quantity: 'other' },
] as const satisfies readonly Unit[]);
